import { inject, injectable } from 'inversify'
import axios from 'axios'
import type { HttpInterceptor, InternalErrorResponse, HttpInterceptorResponseError } from '@/services/transport/types'
import { TmBaseError } from '@/core/error/tmBaseError'
import { TmLostConnectionError } from '@/core/error/transport/tmLostConnectionError'
import { TmCancelRequestError } from '@/core/error/transport/tmCancelRequestError'
import { SERVICE_TYPES } from '@/core/container/types'
import type ErrorSerializerService from '@/services/transport/errorSerializer/errorSerializerService'
import { TmApiError } from '@/core/error/transport/tmApiError'

@injectable()
export default class ApiErrorInterceptor implements HttpInterceptor {
  constructor(
    @inject(SERVICE_TYPES.ErrorSerializerService) protected readonly errorSerializerService: ErrorSerializerService,
  ) {}

  public responseError(error: unknown) {
    if (axios.isCancel(error)) {
      return Promise.reject(new TmCancelRequestError())
    }

    if (this.isResponseError(error)) {
      const ErrorInMap = this.errorSerializerService.getErrorClass(error)
      if (ErrorInMap) {
        const errorInMapInstance = new ErrorInMap(error.message, error.response, error.stack)
        if (errorInMapInstance instanceof TmApiError) {
          errorInMapInstance.setApiMethodInfo({
            method: error.config.method,
            url: error.config.url,
          })
        }
        return Promise.reject(errorInMapInstance)
      }
    }

    let e: Error
    if (axios.isAxiosError<InternalErrorResponse>(error) && error.message?.toLowerCase() === 'network error') {
      e = new TmLostConnectionError(error.message, undefined, error.stack)
    } else if (error instanceof Error) {
      const msg = 'Unknown ApiErrorInterceptor error'
      e = new TmBaseError(`${msg}: ${error.message}`, {}, error.stack)
    } else {
      e = new TmBaseError('Unknown ApiErrorInterceptor error', {})
    }

    // oxlint-disable-next-line prefer-promise-reject-errors -- `e` is always an Error subclass
    return Promise.reject(e)
  }

  private isResponseError(error: unknown): error is HttpInterceptorResponseError<InternalErrorResponse> {
    return error instanceof Error && 'response' in error && typeof error.response !== 'undefined'
  }
}
